import PhoneInput from '@components/elements/phone-input/phone-input';
import { getAccountRegistrationError } from '@crocodille/bbdomu-i18n';
import {
  iife,
  v2_CustomerRegistration,
  v2_useCustomerRegistration,
} from '@crocodille/common';
import { signIn } from 'next-auth/react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ACCOUNT_LOGIN, ACCOUNT_REGISTRATION_COMPLETE } from '../../../routing';
import Button from '../../elements/button/button';
import Error from '../../elements/error/error';
import InputEnvelope from '../../elements/input-envelope/input-envelope';
import Input from '../../elements/input-2/input-2';
import Switch from '../../elements/switch/switch';
import CardIcon from '../../icons/card-icon';
import SpinnerIcon from '../../icons/spinner-icon';
import { AppleButton, FacebookButton, GoogleButton } from '../social/social';

import styles from './account-register-form.module.scss';

export interface AccountRegisterProps {
  readonly enabledProviders?: string[];
  readonly error?: string;
}

const AccountRegisterForm: React.FC<
  React.PropsWithChildren<AccountRegisterProps>
> = ({ enabledProviders = [], error: externalError }) => {
  const { t } = useTranslation();
  const { push } = useRouter();
  const [localError, setLocalError] = useState<string | undefined>(undefined);
  const registeredEmailRef = useRef<string | undefined>(undefined);

  const {
    trigger,
    isMutating: pending,
    error: actionError,
  } = v2_useCustomerRegistration({
    onSuccess: () => {
      push({
        pathname: ACCOUNT_REGISTRATION_COMPLETE,
        query: registeredEmailRef.current
          ? { email: registeredEmailRef.current }
          : undefined,
      });
    },
  });

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const form = e.currentTarget;

      const formData = new FormData(form);

      const credentials = Object.fromEntries(
        Array.from(formData.entries()).map(([key, value]) => {
          let parsedValue: string | null = value.toString().trim();

          if (parsedValue.length === 0) {
            parsedValue = null;
          }

          return [key, parsedValue];
        }),
      );

      if (credentials['password'] !== credentials['passwordConfirmation']) {
        setLocalError('passwords_different');
        return;
      }

      setLocalError(undefined);

      //
      // ! ASSUMPTION: The form contains exactly the same fields as the
      // ! `v2_CustomerRegistration` type.
      //
      const registration = {
        loyaltyCardNumber: null,
        ...credentials,
      } as v2_CustomerRegistration;

      registeredEmailRef.current = registration.email;

      await trigger(registration);
    },
    [trigger],
  );

  const error = getAccountRegistrationError(
    localError ??
      (typeof actionError === 'string'
        ? actionError
        : actionError?.message ?? externalError),
  );

  const errorMessage = iife(() => {
    switch (error) {
      case 'email_conflict':
        return t('register_error.email_conflict');
      case 'phone_number_conflict':
        return t('register_error.phone_number_conflict');
      case 'loyalty_card_conflict':
        return t('register_error.loyalty_card_conflict');
      case 'account_exists_via_provider':
        return t('register_error.account_exists_via_provider');
      case 'passwords_different':
        return t('register_error.passwords_different');
      case 'other':
        return t('register_error.other');
      default:
        return null;
    }
  });

  const [ownsLoyaltyCard, setOwnsLoyaltyCard] = useState(false);

  return (
    <div className={styles.wrapper}>
      <h1 className={styles.formTitle}>{t('account.register_title')}</h1>
      <p className={styles.formIntro}>{t('account.register_text')}</p>
      <p className={styles.formIntro2}>{t('account.register_text2')}</p>
      {enabledProviders.includes('facebook') ? (
        <FacebookButton
          className={styles.providerButton}
          onClick={() => signIn('facebook')}
        />
      ) : null}
      {enabledProviders.includes('google') ? (
        <GoogleButton
          className={styles.providerButton}
          onClick={() => signIn('google')}
        />
      ) : null}
      {enabledProviders.includes('apple') ? (
        <AppleButton
          className={styles.providerButton}
          onClick={() => signIn('apple')}
        />
      ) : null}
      {enabledProviders.length > 0 ? (
        <div className={styles.loginDivider}>
          <span>{t('account.login_or')}</span>
        </div>
      ) : null}
      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.formWrapper}>
          <InputEnvelope
            label={t('account.first_name')}
            required
            noLabelElement
          >
            <Input
              type="text"
              name="firstName"
              autoComplete="given-name"
              minLength={2}
              maxLength={30}
              required
              placeholder={t('account.first_name')}
            />
          </InputEnvelope>
          <InputEnvelope label={t('account.last_name')} required noLabelElement>
            <Input
              type="text"
              name="lastName"
              autoComplete="family-name"
              minLength={2}
              maxLength={30}
              required
              placeholder={t('account.last_name')}
            />
          </InputEnvelope>
          <PhoneInput
            label={t('account.phone_number')}
            required
            name="phoneNumber"
          />
          <InputEnvelope label={t('account.email')} required noLabelElement>
            <Input
              type="email"
              name="email"
              maxLength={70}
              autoComplete="email"
              required
              placeholder={t('account.email')}
            />
          </InputEnvelope>

          <InputEnvelope label={t('account.password')} required noLabelElement>
            <Input
              type="password"
              name="password"
              autoComplete="new-password"
              minLength={4}
              required
              placeholder={t('account.password')}
            />
          </InputEnvelope>

          <InputEnvelope
            label={t('account.verify_password')}
            required
            noLabelElement
          >
            <Input
              type="password"
              name="passwordConfirmation"
              autoComplete="new-password"
              minLength={4}
              placeholder={t('account.verify_password')}
            />
          </InputEnvelope>
          <InputEnvelope label={t('account.has_loyalty_card')} inline>
            <Switch value={ownsLoyaltyCard} onChange={setOwnsLoyaltyCard} />
          </InputEnvelope>
          {ownsLoyaltyCard ? (
            <>
              <p className={styles.formLoyalty}>
                {t('account.register_loyalty')}
              </p>
              <div className={styles.cardIconWrapper}>
                <CardIcon className={styles.cardIcon} />
              </div>
              <InputEnvelope
                label={t('account.loyalty_card_number')}
                required
                noLabelElement
              >
                <Input
                  type="text"
                  name="loyaltyCardNumber"
                  autoComplete="off"
                  required
                  placeholder={t('account.loyalty_card_number')}
                />
              </InputEnvelope>
            </>
          ) : null}
        </div>
        {errorMessage ? <Error>{errorMessage}</Error> : null}
        <div className={styles.buttonWrapper}>
          <Button type="submit" disabled={pending} round>
            {t('account.register')}
            <Button.Icon>
              <Button.Icon>{pending ? <SpinnerIcon /> : null}</Button.Icon>
            </Button.Icon>
          </Button>
        </div>
      </form>
      <Link href={ACCOUNT_LOGIN} className={styles.loginLink} prefetch={false}>
        {t('account.register_login')}
      </Link>
    </div>
  );
};

export default AccountRegisterForm;
